import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IUser } from '@core/models';
import { User } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';
import { touchControls } from '@shared/utils';
import { ConfirmPasswordValidator } from '@shared/validators';
import { firstValueFrom } from 'rxjs';
import { FormErrorComponent } from '@shared/components/form-error/form-error.component';

@Component({
  selector: 'app-change-password',
  imports: [TranslateModule, MatDialogModule, ReactiveFormsModule, FormErrorComponent],
  templateUrl: "./change-password.component.html",
  styleUrl: "./change-password.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangePassword {
  public readonly dialogRef = inject(MatDialogRef<ChangePassword>);
  public readonly data = inject(MAT_DIALOG_DATA) as { userProfile: IUser };
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(User);
  /** Controls visibility of the current password field */
  protected showNowPassword = signal(false);
  /** Controls visibility of the new password field */
  protected showNewPassword = signal(false);
  /** Controls visibility of the confirm password field */
  protected showRePassword = signal(false);
  /** Stores server-side validation errors for each form field */
  protected serverErrors = signal<{ [key: string]: string[] }>({});

  /** Variable que guardará los datos del formulario para cambio de constraseña.*/
  protected readonly formChange = signal<FormGroup>(this.createForm());
  /**variable que guardará los datos del usuario */
  private user: IUser;
  /** @ignore*/

  // cp = false;
  /**
   * Toggles the visibility of the current password input field.
   * When executed, it switches between showing and hiding the current password.
   */
  protected toggleNowPassword() {
    this.showNowPassword.set(!this.showNowPassword());
  }

  /**
   * Toggles the visibility of the new password input field.
   * When executed, it switches between showing and hiding the new password.
   */
  protected toggleNewPassword() {
    this.showNewPassword.set(!this.showNewPassword());
  }

  /**
   * Toggles the visibility of the repeat (confirmation) password input field.
   * When executed, it switches between showing and hiding the confirmation password.
   */
  protected toggleRePassword() {
    this.showRePassword.set(!this.showRePassword());
  }

  /**
   * Toggles the visibility of the repeat (confirmation) password input field.
   * When executed, it switches between showing and hiding the confirmation password.
   */
  ngOnInit() {
    const form = this.formChange();
    form.get('now_password')?.valueChanges.subscribe(() => {
      const errors = this.serverErrors();
      if (errors?.now_password) {
        delete errors.now_password;
        this.serverErrors.set({ ...errors });
      }
    });
  }
  /**
   * Función encargada de inicializar variables y ejecutar funciones en el momento que el componente es requerido, esto para
   * que a la hora de iniciar la carga ya cuente con valores y se púedan proporcionar las funcionalidades necesarias.
   * @param dialogRef Parametro que tendra las funcionalidades de MatDialogRef
   * @param data Objetro que recibira la información del usuario desde el componente que lo haya convocado.
   * @param fb Parametro que tendra las funcionalidades de FormBuilder.
   * @param userService Parametro que tendra las funcionalidades de UserService.
   */
  constructor() {
    this.user = this.data.userProfile;
  }

  /**
   * Función encargada de crear los campos para el formulario, ademas de agregar las validaciones
   * necesarias para el correcto funcionamiento del mismo, este en si afecta a los controls que
   * acompañan al html con el tag formcControlName.
   */
  private createForm(): FormGroup {
    return this.fb.group(
      {
        now_password: [null, Validators.required],
        password: [null, [Validators.required, Validators.minLength(6)]],
        re_password: [null, Validators.required]
      },
      {
        validator: ConfirmPasswordValidator.MatchPassword
      }
    );
  }

  /**
   * Función que activa una vez el usuario ha hecho clic en el botón confirmar y lo
   * que hace es llamar al metodo que va a cambiar la contraseña, esto solo si exites en usuario
   * de lo contrario notificara un fail.
   */
  protected close(): void {
    const form = this.formChange();
    if (form.invalid) {
      touchControls(form);
      return;
    }
    if (this.user) {
      this.changePassword(this.user);
    } else {
      this.dialogRef.close('fail');
    }
  }

  /**
   * Función asincrona encargada de realizar el cambio de contraseña, aqui se capturan los elmentos del formulario
   * y se ejecuta al metedo edit() pertenciente al servicio UserServices quien es el encargado de realizar la debida peticion.
   * @param user parametro con los datos del usuario.
   */
  private async changePassword(user: IUser) {
    const form = this.formChange();
    const {
      image_profile, ...newUser
    } = user;

    newUser.now_password = form.get('now_password')?.value;
    newUser.re_password = form.get('re_password')?.value;
    newUser.password = form.get('password')?.value;

    try {
      const response = await firstValueFrom(this.userService.edit(Number(newUser.id), newUser));
      this.dialogRef.close('exito');
    } catch (error: any) {
      console.error(error);
      if (error && error.error && typeof error.error === 'object') {
        this.serverErrors.set(error.error);
      } else {
        this.serverErrors.set({});
      }
    }
  }
}
